import api from './api'
import { saveBlob } from '../../lib/download'
import { filenameFromContentDisposition } from './document-pdf'

/**
 * PRE-SHIP QUESTIONNAIRES (2026-10-03) — the form a customer fills in before
 * XOP ships an appliance (network, rack and power, SIP trunk, installer
 * requests). It used to be one of three spreadsheets mailed back and forth; it
 * is now a per-customer document in crm-api, edited here by staff and answered
 * by the customer in the support portal.
 *
 * It is the spreadsheet (CEO, 2026-10-03): titled sections of lines. No
 * templates, no status. A new one starts with the big sections (Basic
 * information pre-filled, the rest empty); the sign-off is a fixed block, not
 * a section.
 *
 * Feedback, 2026-10-23: a line's description is HELP TEXT behind an ⓘ (not a
 * column); a line has a VALUE TYPE crm-api enforces (`valueError` mirrors
 * PreShip::ValueTypes for instant feedback); and staff can SHARE a no-login
 * link with the customer (`share` / `unshare`).
 *
 * crm-api owns it: `/api/v1/pre_ship_questionnaires*` (staff), Customers page
 * to read, `crm:edit` to write, scoped like the customer list. The PDF is
 * crm-api's ONE XOP-branded rendering — the browser never draws it.
 *
 * Contract: docs/crm.md → "Pre-ship questionnaires".
 */

export type PreShipAddedBy = 'staff' | 'customer'

/** What a line's value may hold — crm-api's PreShip::ValueTypes. */
export type PreShipValueType = 'text' | 'number' | 'yes_no' | 'date' | 'email' | 'phone' | 'ip_address' | 'choice'

export const VALUE_TYPES: ReadonlyArray<{ value: PreShipValueType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'ip_address', label: 'IP address' },
  { value: 'choice', label: 'List of values' },
]

/**
 * One line of the spreadsheet: WHAT it is, and the VALUE. `description` is the
 * line's help text, shown behind an ⓘ (2026-10-23). `type` / `choices` are
 * absent on lines stored before value types existed — read them through
 * `typeOf`.
 */
export interface PreShipLine {
  id: string
  label: string
  description: string
  value: string
  type?: PreShipValueType
  /** Allowed values of a `choice` line. */
  choices?: string[]
  /** Who added the line. The customer may change only its own lines' label/description. */
  added_by: PreShipAddedBy
}

export interface PreShipSection {
  id: string
  title: string
  rows: PreShipLine[]
}

export interface PreShipProgress {
  answered: number
  total: number
}

/** A live link staff send the customer; null when there is none. */
export interface PreShipShare {
  url: string
  expires_at: string
  created_by_email: string | null
}

export interface PreShipSummary {
  id: number
  customer_id: number
  customer_name: string | null
  title: string
  progress: PreShipProgress
  lock_version: number
  created_by_email: string | null
  updated_by_email: string | null
  updated_by_kind: 'staff' | 'customer' | null
  created_at: string
  updated_at: string
  share?: PreShipShare | null
}

/**
 * The sign-off — completed by, signature, date. NOT a section (CEO,
 * 2026-10-04): a fixed block at the foot of the form and the PDF, filled in
 * by staff or the customer.
 */
export interface PreShipSignOff {
  completed_by: string
  signature: string
  signed_on: string
}

export const EMPTY_SIGN_OFF: PreShipSignOff = { completed_by: '', signature: '', signed_on: '' }

export interface PreShipQuestionnaire extends PreShipSummary {
  intro: string
  sections: PreShipSection[]
  sign_off: PreShipSignOff
}

export interface PreShipListParams {
  page?: number
  per_page?: number
  q?: string
  customer_id?: number | string
}

export interface PreShipPagination {
  current_page: number
  per_page: number
  total_pages: number
  total_count: number
  has_next: boolean
  has_prev: boolean
}

export interface PreShipListResponse {
  pre_ship_questionnaires: PreShipSummary[]
  meta: { pagination: PreShipPagination }
}

export interface PreShipPatch {
  lock_version?: number
  title?: string
  sections?: PreShipSection[]
  sign_off?: PreShipSignOff
}

export const PRE_SHIP_PER_PAGE = 25

/** crm-api's code for "someone saved this since you loaded it". */
export const PRE_SHIP_STALE = 'QUESTIONNAIRE_STALE'

/** crm-api's code for "these values do not fit their lines' types". */
export const PRE_SHIP_INVALID_VALUES = 'INVALID_VALUES'

function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/** A blank line staff add to a section. crm-api stamps `added_by`. */
export function newLine(): PreShipLine {
  return { id: newId('r'), label: '', description: '', value: '', type: 'text', choices: [], added_by: 'staff' }
}

export function typeOf(row: Pick<PreShipLine, 'type'>): PreShipValueType {
  return VALUE_TYPES.some((t) => t.value === row.type) ? (row.type as PreShipValueType) : 'text'
}

const NUMBER_FORMAT = /^-?\d+(\.\d+)?$/
const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/
const EMAIL_FORMAT = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const PHONE_FORMAT = /^\+?[\d\s().\-/]+(\s*(x|ext\.?)\s*\d{1,6})?$/i
const IPV4_FORMAT = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/

function validDate(value: string): boolean {
  if (!DATE_FORMAT.test(value)) return false
  const [y, m, d] = value.split('-').map(Number) as [number, number, number]
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

function validPhone(value: string): boolean {
  if (!PHONE_FORMAT.test(value)) return false
  const digits = value.replace(/(x|ext\.?)\s*\d+$/i, '').replace(/\D/g, '').length
  return digits >= 7 && digits <= 15
}

/** IPv6 is checked by the browser's own URL parser — the same strictness as Ruby's IPAddr for anything staff will type. */
function validIpv6(value: string): boolean {
  if (!value.includes(':') || !/^[0-9a-f:.]+$/i.test(value)) return false
  try {
    return new URL(`http://[${value}]/`).hostname.length > 2
  } catch {
    return false
  }
}

/**
 * The sentence shown under a value that does not fit its line's type, or
 * null. Mirrors crm-api's PreShip::ValueTypes#error_for (which decides — this
 * is only instant feedback). A blank value always passes.
 */
export function valueError(type: PreShipValueType, raw: string, choices: string[] = []): string | null {
  const value = raw.trim()
  if (value === '') return null
  switch (type) {
    case 'number':
      return NUMBER_FORMAT.test(value) ? null : 'Enter a number.'
    case 'yes_no':
      return value === 'Yes' || value === 'No' ? null : 'Choose Yes or No.'
    case 'date':
      return validDate(value) ? null : 'Enter a date.'
    case 'email':
      return EMAIL_FORMAT.test(value) ? null : 'Enter an email address.'
    case 'phone':
      return validPhone(value) ? null : 'Enter a phone number.'
    case 'ip_address':
      return IPV4_FORMAT.test(value) || validIpv6(value) ? null : 'Enter an IP address, like 192.168.1.10.'
    case 'choice':
      return choices.length === 0 || choices.includes(value) ? null : 'Choose one of the listed values.'
    default:
      return null
  }
}

/** Every line whose value does not fit, as row id → message. */
export function valueErrors(sections: PreShipSection[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const row of sections.flatMap((s) => s.rows)) {
    const message = valueError(typeOf(row), row.value, row.choices ?? [])
    if (message) out[row.id] = message
  }
  return out
}

/** crm-api's 422 INVALID_VALUES as row id → message; null for any other error. */
export function invalidValuesOf(err: unknown): Record<string, string> | null {
  const data = (err as { response?: { data?: { code?: string; errors?: Array<{ row_id: string; message: string }> } } })?.response?.data
  if (data?.code !== PRE_SHIP_INVALID_VALUES) return null
  return Object.fromEntries((data.errors ?? []).map((e) => [e.row_id, e.message]))
}

export function newSection(title: string): PreShipSection {
  return { id: newId('s'), title, rows: [newLine()] }
}

/** "12 of 40 filled in" — the same count crm-api serves on the list. */
export function progressOf(sections: PreShipSection[]): PreShipProgress {
  const rows = sections.flatMap((s) => s.rows)
  return { answered: rows.filter((r) => r.value.trim() !== '').length, total: rows.length }
}

/** Move an item one place up (-1) or down (+1); out of range is a no-op. */
export function move<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta
  if (target < 0 || target >= list.length) return list
  const next = [...list]
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item as T)
  return next
}

export function preShipQuery(params: PreShipListParams = {}): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    query.append(key, String(value))
  }
  const text = query.toString()
  return text ? `?${text}` : ''
}

export function isStaleError(err: unknown): boolean {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code === PRE_SHIP_STALE
}

class PreShipApi {
  async list(params: PreShipListParams = {}): Promise<PreShipListResponse> {
    const response = await api.get(`/pre_ship_questionnaires${preShipQuery(params)}`)
    return response.data
  }

  async get(id: number | string): Promise<PreShipQuestionnaire> {
    const response = await api.get(`/pre_ship_questionnaires/${id}`)
    return response.data.pre_ship_questionnaire
  }

  /** Always the same start: the Basic information section, pre-filled by crm-api. */
  async create(input: { customer_id: number | string; title?: string }): Promise<PreShipQuestionnaire> {
    const response = await api.post('/pre_ship_questionnaires', input)
    return response.data.pre_ship_questionnaire
  }

  async update(id: number | string, patch: PreShipPatch): Promise<PreShipQuestionnaire> {
    const response = await api.patch(`/pre_ship_questionnaires/${id}`, patch)
    return response.data.pre_ship_questionnaire
  }

  /** A new no-login link for the customer; any earlier link stops working. */
  async share(id: number | string): Promise<PreShipQuestionnaire> {
    const response = await api.post(`/pre_ship_questionnaires/${id}/share`)
    return response.data.pre_ship_questionnaire
  }

  /** The link stops working. */
  async unshare(id: number | string): Promise<PreShipQuestionnaire> {
    const response = await api.delete(`/pre_ship_questionnaires/${id}/share`)
    return response.data.pre_ship_questionnaire
  }

  /** Soft delete. */
  async remove(id: number | string): Promise<void> {
    await api.delete(`/pre_ship_questionnaires/${id}`)
  }

  /**
   * Bytes for `<PdfReader>`. `.slice(0)` is a COPY on purpose: pdf.js DETACHES
   * the buffer it is handed (the "Buffer is already detached" crash).
   */
  async pdfBytes(id: number | string): Promise<ArrayBuffer> {
    const response = await api.get(`/pre_ship_questionnaires/${id}/pdf`, { responseType: 'arraybuffer' })
    return (response.data as ArrayBuffer).slice(0)
  }

  /** Save crm-api's PDF under crm-api's own name ("Acme - Site A.pdf"). */
  async downloadPdf(q: Pick<PreShipSummary, 'id' | 'title'>): Promise<void> {
    const response = await api.get(`/pre_ship_questionnaires/${q.id}/pdf?disposition=attachment`, { responseType: 'blob' })
    const header = response.headers?.['content-disposition']
    const name = (typeof header === 'string' ? filenameFromContentDisposition(header) : null) ?? `${q.title || 'Pre-ship questionnaire'}.pdf`
    await saveBlob(name, new Blob([response.data], { type: 'application/pdf' }))
  }
}

export const preShipApi = new PreShipApi()
